/**
 * מחיר טיסה בתוצאות חיפוש חבילה דינמית — המינימום ממבחר ה-deals מ־LDS searchFlights
 * (אותם תאריכים/הרכב כמו החיפוש), לא מ־savedDeal / בחירה בפרופיל.
 */

import {
  extractDynamicPackageDealFareComponents,
  pickDynamicPackageSupplierCurrency,
} from "./dynamicPackageNormalizedPricing";

function strictPositiveNumber(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function isIso4217(c) {
  if (c == null || String(c).trim() === "") return false;
  return /^[A-Z]{3}$/.test(String(c).trim().toUpperCase());
}

export function toDealArray(x) {
  if (x == null) return [];
  if (Array.isArray(x)) return x;
  return [x];
}

/**
 * מחיר טיסה לתצוגה מ-deal של searchFlights בלבד (ללא hotelFare וכו').
 * @returns {{ amount: number|null, currency: string|null, source: string|null }}
 */
export function extractFlightFareFromLdsFlightSearchDeal(deal) {
  if (!deal || typeof deal !== "object") {
    return { amount: null, currency: null, source: null };
  }
  const comp = extractDynamicPackageDealFareComponents(deal);
  if (comp.flight != null) {
    const cur = pickDynamicPackageSupplierCurrency(deal);
    return {
      amount: comp.flight,
      currency: isIso4217(cur) ? String(cur).trim().toUpperCase() : null,
      source: "dealFare_flight_fields",
    };
  }

  const df = deal.dealFare;
  if (!df || typeof df !== "object") {
    return { amount: null, currency: null, source: null };
  }

  const hasHotelish =
    strictPositiveNumber(df.hotelFare) != null ||
    strictPositiveNumber(df.roomFare) != null ||
    strictPositiveNumber(df.accommodationFare) != null;

  if (hasHotelish) {
    return { amount: null, currency: null, source: "deal_has_hotel_fare_skip" };
  }

  const total = strictPositiveNumber(df.totalFare);
  if (total == null) {
    return { amount: null, currency: null, source: "no_totalFare" };
  }

  const cur = pickDynamicPackageSupplierCurrency(deal);
  return {
    amount: total,
    currency: isIso4217(cur) ? String(cur).trim().toUpperCase() : null,
    source: "dealFare_totalFare_flight_only",
  };
}

function minFromDealList(deals, traceSource) {
  const list = toDealArray(deals).filter((d) => d && typeof d === "object");
  if (list.length === 0) {
    return {
      amount: null,
      currency: null,
      candidate: null,
      unavailableReason: "empty_deal_list",
      source: traceSource,
    };
  }

  const priced = [];
  for (const d of list) {
    const { amount, currency, source } = extractFlightFareFromLdsFlightSearchDeal(d);
    if (amount == null || !isIso4217(currency)) continue;
    priced.push({ amount, currency, deal: d, innerSource: source });
  }

  if (priced.length === 0) {
    return {
      amount: null,
      currency: null,
      candidate: null,
      unavailableReason: "no_priced_flight_deals",
      source: traceSource,
    };
  }

  const currencies = new Set(priced.map((p) => p.currency));
  if (currencies.size > 1) {
    return {
      amount: null,
      currency: null,
      candidate: null,
      unavailableReason: "mixed_supplier_currencies",
      source: traceSource,
    };
  }

  const best = priced.reduce((a, b) => (a.amount <= b.amount ? a : b));
  return {
    amount: best.amount,
    currency: best.currency,
    candidate: best.deal,
    unavailableReason: null,
    source: `${traceSource}:${best.innerSource}`,
  };
}

/**
 * מינימום סכום (הלוך+חזור) מזוגות OW, רק כשמטבעות תואמים.
 */
function minComposedRoundTrip(outDeals, inDeals, traceSource) {
  const outs = toDealArray(outDeals);
  const ins = toDealArray(inDeals);
  if (outs.length === 0 || ins.length === 0) {
    return {
      amount: null,
      currency: null,
      candidate: null,
      unavailableReason: "missing_out_or_in_list",
      source: traceSource,
    };
  }

  const candidates = [];
  for (const o of outs) {
    const po = extractFlightFareFromLdsFlightSearchDeal(o);
    if (po.amount == null || !isIso4217(po.currency)) continue;
    for (const i of ins) {
      const pi = extractFlightFareFromLdsFlightSearchDeal(i);
      if (pi.amount == null || !isIso4217(pi.currency)) continue;
      if (po.currency !== pi.currency) continue;
      candidates.push({
        amount: po.amount + pi.amount,
        currency: po.currency,
        out: o,
        inn: i,
        innerSource: `${po.source}+${pi.source}`,
      });
    }
  }

  if (candidates.length === 0) {
    return {
      amount: null,
      currency: null,
      candidate: null,
      unavailableReason: "no_valid_composed_pairs",
      source: traceSource,
    };
  }

  const best = candidates.reduce((a, b) => (a.amount <= b.amount ? a : b));
  return {
    amount: best.amount,
    currency: best.currency,
    candidate: { outbound: best.out, inbound: best.inn },
    unavailableReason: null,
    source: `${traceSource}:${best.innerSource}`,
  };
}

/**
 * @param {object} p
 * @param {"RoundTrip"|"OneWay"|string} [p.flightTripType]
 * @param {unknown} [p.roundTripFlightResults]
 * @param {unknown} [p.outboundOneWayFlightResults]
 * @param {unknown} [p.inboundOneWayFlightResults]
 * @param {unknown} [p.availableFlights]
 * @returns {{
 *   amount: number|null,
 *   currency: string|null,
 *   candidate: unknown,
 *   unavailableReason: string|null,
 *   source: string|null,
 *   rawFlightSources: object,
 * }}
 */
export function pickCheapestDynamicPackageSearchFlightPrice(p = {}) {
  const {
    flightTripType = "RoundTrip",
    roundTripFlightResults = null,
    outboundOneWayFlightResults = null,
    inboundOneWayFlightResults = null,
    availableFlights = null,
  } = p;

  const rawFlightSources = {
    flightTripType,
    roundTripCount: toDealArray(roundTripFlightResults).length,
    outboundOwCount: toDealArray(outboundOneWayFlightResults).length,
    inboundOwCount: toDealArray(inboundOneWayFlightResults).length,
    availableFlightsCount: toDealArray(availableFlights).length,
  };

  if (flightTripType === "OneWay") {
    const r = minFromDealList(availableFlights, "availableFlights_oneWay");
    return { ...r, rawFlightSources };
  }

  const rt = minFromDealList(roundTripFlightResults, "roundTripFlightResults");
  if (rt.amount != null) {
    return { ...rt, rawFlightSources };
  }
  if (rt.unavailableReason === "mixed_supplier_currencies") {
    return { ...rt, rawFlightSources };
  }

  const composed = minComposedRoundTrip(
    outboundOneWayFlightResults,
    inboundOneWayFlightResults,
    "composed_OW_out_plus_in"
  );
  if (composed.amount != null) {
    return { ...composed, rawFlightSources };
  }

  const outboundOnly = minFromDealList(
    availableFlights,
    "availableFlights_fallback_outbound_only"
  );
  if (outboundOnly.amount != null) {
    return {
      ...outboundOnly,
      unavailableReason: outboundOnly.unavailableReason ?? "partial_one_way_list_only",
      source: outboundOnly.source,
      rawFlightSources,
    };
  }

  return {
    amount: null,
    currency: null,
    candidate: null,
    unavailableReason:
      composed.unavailableReason ||
      rt.unavailableReason ||
      "no_flight_price_source",
    source: null,
    rawFlightSources,
  };
}
